import { MetricTypes } from "../autogen/metrics";
import { getMetricData, MetricAggregationType } from "../autogen/aggregation-data";
import type { DataPoint } from "../utils/fetch-api";

import { PaidChannel } from "./paid-channels";

const safeDivide = (num?: number, denom?: number) => {
  if (!denom) {
    return 0;
  }
  return (num || 0) / denom;
};

export const getChannelMetric = (metric: MetricTypes, channel: PaidChannel): MetricTypes => {
  const channelPrefix = {
    [PaidChannel.META_ADS]: "META",
    [PaidChannel.GOOGLE_ADS]: "GOOGLE",
  }[channel];
  return `${channelPrefix}_${metric}` as MetricTypes;
};

export const getSummaryData = (data: DataPoint, metrics: MetricTypes[]) => {
  // Reader beware! Those code adds together metrics in a way that doesn't make dimensional sense.
  // Adding e.g. meta conversions to google conversions is ill formed if they use different attribution windows.
  // While they are not logically comparable, as a rough measure of bigness it's the best we can do at this time...

  const rv = { ...data };

  const allSimpleMetrics = Array.from(
    new Set(
      metrics.reduce((acc, metric) => {
        const metricData = getMetricData(metric as MetricTypes);
        if (metricData.type === MetricAggregationType.Ratio) {
          return acc.concat([metricData.numerator, metricData.denominator]);
        } else {
          return acc.concat([metric]);
        }
      }, [] as MetricTypes[])
    )
  );

  allSimpleMetrics.forEach((metric: MetricTypes) => {
    rv[metric] = Object.keys(PaidChannel).reduce((acc, channel: string) => {
      const channelMetric = getChannelMetric(metric, channel as PaidChannel);
      const channelMetricValue = +(data[channelMetric] ?? 0);
      return acc + channelMetricValue;
    }, 0);
  });

  // Special

  metrics.map((metric) => {
    const metricData = getMetricData(metric as MetricTypes);
    if (metricData.type === MetricAggregationType.Ratio) {
      rv[metric] = safeDivide((rv[metricData.numerator] as number) || 0, (rv[metricData.denominator] as number) || 0);
    }
  });

  const totalSpend = Object.keys(PaidChannel).reduce((acc, channel: string) => {
    const channelMetric = getChannelMetric(MetricTypes.AD_SPEND, channel as PaidChannel);
    const channelMetricValue = +(data[channelMetric] ?? 0);
    return acc + channelMetricValue;
  }, 0);

  rv[MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND] = 1;

  Object.keys(PaidChannel).forEach((_channel: string) => {
    const channel = _channel as PaidChannel;
    const channelPercentSpendMetric = getChannelMetric(MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND, channel);
    const channelSpendMetric = getChannelMetric(MetricTypes.AD_SPEND, channel);
    rv[channelPercentSpendMetric] = safeDivide((data[channelSpendMetric] as number) || 0, totalSpend);
  });

  return rv;
};
